import { get } from '@ember/object';
import Route from '@ember/routing/route';
import { service } from '@ember/service';

import window from 'ember-window-mock';

import { variation } from 'ember-launch-darkly';

import { registerJsURL } from 'register-partners-js/constants/hosts';
import { SUBMITTED_STATUSES } from 'register-partners-js/constants/registration-statuses';
import { hasCompanyActivityInfo } from 'register-partners-js/utils/company-activity';
import { getRedirectionToken } from 'register-partners-js/utils/get-redirection-token';

function isFrenchNewActivityScreenEnabled(organization) {
  return (
    organization.get('legalCountry') === 'FR' &&
    variation('feature--boolean-fr-new-activity-screen')
  );
}

function needsCompanyActivityStep(organization) {
  return isFrenchNewActivityScreenEnabled(organization) && !hasCompanyActivityInfo(organization);
}

export default class RegistrationIndexRoute extends Route {
  @service router;
  @service store;
  @service apiFlowManager;
  @service currentUser;

  redirect(registration) {
    if (SUBMITTED_STATUSES.includes(registration.status)) {
      // Drop the redirection token so the resume index doesn't loop us back here.
      let token = getRedirectionToken(this.store);
      if (token) this.store.unloadRecord(token);
      this.router.replaceWith('registrations');
      return;
    }

    if (registration.status === 'contract_sign_pending') {
      this.router.replaceWith('registration.pending-contract');
    } else if (registration.flowType === 'manual') {
      let url = new URL(registerJsURL);
      url.searchParams.set('skip', 'true');
      // Disambiguates which registration triggered `?skip=true`: a user can have several
      // (same email, different SIREN), and register-js otherwise has no way to tell which
      // one to redirect to (ZD-881).
      url.searchParams.set('partner_registration_id', registration.id);

      window.location.replace(url);
    } else if (registration.legalFlow === 'existing_company') {
      this.landOnExistingCompanyLastCompletedStep(registration);
    } else {
      this.landOnCompanyCreationLastCompletedStep(registration);
    }
  }

  landOnCompanyCreationLastCompletedStep(registration) {
    let { organization, owner, isPaid, prepaidSource } = registration;
    let natureOfOperations = organization.get('natureOfOperations');
    let yearlyTransactionVolume = organization.get('yearlyTransactionVolume');
    let maxSinglePaymentAmount = organization.get('maxSinglePaymentAmount');

    let hasNatureOfOpsInfo = Boolean(natureOfOperations?.length);
    let hasBusinessFinancesInfo =
      Boolean(yearlyTransactionVolume) && Boolean(maxSinglePaymentAmount);

    let hasOwnerTaxInfo =
      typeof owner?.get('taxPayerOutsideLegalCountry') === 'boolean' &&
      typeof owner?.get('usTaxPayer') === 'boolean';

    if (isPaid && !prepaidSource) {
      this.router.replaceWith('registration.almost-ready');
    } else if (
      hasOwnerTaxInfo &&
      hasNatureOfOpsInfo &&
      hasBusinessFinancesInfo &&
      !needsCompanyActivityStep(organization)
    ) {
      this.router.replaceWith('react', `registrations/${registration.id}/owner-info`);
    } else if (
      hasNatureOfOpsInfo &&
      !needsCompanyActivityStep(organization) &&
      (hasBusinessFinancesInfo || isFrenchNewActivityScreenEnabled(organization))
    ) {
      this.router.replaceWith('registration.business-finances');
    } else if (hasNatureOfOpsInfo && needsCompanyActivityStep(organization)) {
      this.router.replaceWith('react', `registrations/${registration.id}/company-activity`);
    } else if (hasNatureOfOpsInfo) {
      this.router.replaceWith('registration.nature-of-ops');
    } else {
      this.router.replaceWith('registration.start');
    }
  }

  async landOnExistingCompanyLastCompletedStep(registration) {
    let organization = await registration.get('organization');
    let owner = await organization.owner;

    let natureOfOperations = organization.get('natureOfOperations');
    let yearlyTransactionVolume = organization.get('yearlyTransactionVolume');
    let maxSinglePaymentAmount = organization.get('maxSinglePaymentAmount');
    let originalPricePlanCode = organization.get('originalPricePlanCode');

    let hasNatureOfOpsInfo = Boolean(natureOfOperations?.length);
    let hasBusinessFinancesInfo =
      Boolean(yearlyTransactionVolume) && Boolean(maxSinglePaymentAmount);

    let hasOwnerTaxInfo =
      typeof owner?.get('taxPayerOutsideLegalCountry') === 'boolean' &&
      typeof owner?.get('usTaxPayer') === 'boolean';

    let hasPhoneNumber = this.currentUser.user.phoneNumber && owner.phoneNumber;
    if (!owner) {
      owner = registration.get('stakeholders')?.find(item => get(item, 'owner') === true);
    }
    let hasAnyPhoneNumber = this.currentUser.user.phoneNumber || owner?.phoneNumber;
    let isMicroEnterpriseCreationThoughLegalPlace =
      registration.isMicroEnterpriseCreationThoughLegalPlace;

    if (originalPricePlanCode) {
      this.router.replaceWith('registration.order-summary');
    } else if (
      hasOwnerTaxInfo &&
      hasBusinessFinancesInfo &&
      hasNatureOfOpsInfo &&
      hasPhoneNumber &&
      !needsCompanyActivityStep(organization)
    ) {
      this.router.replaceWith('react', `registrations/${registration.id}/owner-info`);
    } else if (
      hasNatureOfOpsInfo &&
      hasPhoneNumber &&
      !needsCompanyActivityStep(organization) &&
      (hasBusinessFinancesInfo || isFrenchNewActivityScreenEnabled(organization))
    ) {
      this.router.replaceWith('registration.business-finances');
    } else if (hasNatureOfOpsInfo && hasPhoneNumber && needsCompanyActivityStep(organization)) {
      this.router.replaceWith('react', `registrations/${registration.id}/company-activity`);
    } else if (hasNatureOfOpsInfo && hasPhoneNumber) {
      this.router.replaceWith('registration.nature-of-ops');
    } else if (hasPhoneNumber || (isMicroEnterpriseCreationThoughLegalPlace && hasAnyPhoneNumber)) {
      // hasPhoneNumber sends the user to the phone number form if BOTH the user and the owner have
      // a phone number defined. There is another case where the user is coming from the Legal Place
      // flow with a micro enterprise, in which case we want to go to the phone number form and skip
      // the credentials step if any phone number is found.
      this.router.replaceWith('registration.phone-number');
    } else {
      this.router.replaceWith('registration.credentials-success');
    }
  }
}
